/**
 * One-time auth state cleanup utility
 * Clears corrupted auth state from error monitoring system
 * This will run once per user and then disable itself
 */

const AUTH_CLEANUP_VERSION = '1.2.0';
const CLEANUP_KEY = 'auth_state_cleaned';

export const performAuthStateCleanup = () => {
  try {
    // Check if cleanup has already been performed for this version
    const cleanupPerformed = localStorage.getItem(CLEANUP_KEY);
    if (cleanupPerformed === AUTH_CLEANUP_VERSION) {
      console.log('[AuthCleanup] Auth state already cleaned for this version');
      return;
    }

    console.log('[AuthCleanup] Performing one-time auth state cleanup...');
    
    // AGGRESSIVE CLEANUP: Clear ALL potentially corrupted Supabase auth data
    console.log('[AuthCleanup] 🧹 AGGRESSIVE AUTH CLEANUP for sign-out fix');
    
    // Clear all Supabase auth tokens (they will be refreshed on next sign-in)
    const supabaseAuthKeys = [
      'supabase.auth.token',
      'sb-refresh-token',
      'sb-access-token',
      'sb-user-session',
      'supabase-auth-token',
      'auth-token'
    ];
    
    supabaseAuthKeys.forEach(key => {
      try {
        if (localStorage.getItem(key)) {
          console.log(`[AuthCleanup] 🗑️ Clearing potentially corrupted auth key: ${key}`);
          localStorage.removeItem(key);
        }
        if (sessionStorage.getItem(key)) {
          console.log(`[AuthCleanup] 🗑️ Clearing potentially corrupted session auth key: ${key}`);
          sessionStorage.removeItem(key);
        }
      } catch (err) {
        console.warn(`[AuthCleanup] Could not clear ${key}:`, err);
      }
    });
    
    // Clear any cached profiles that might be stale
    try {
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('verifiedProfile-')) {
          console.log(`[AuthCleanup] 🗑️ Clearing cached profile: ${key}`);
          sessionStorage.removeItem(key);
        }
      });
    } catch (err) {
      console.warn('[AuthCleanup] Could not clear cached profiles:', err);
    }
    
    // List of other potentially corrupted keys that might exist from error monitoring
    const suspiciousKeys = [
      // Error monitoring related
      'error_monitoring_state',
      'error_log_cache',
      'performance_data',
      'auth_error_state',
      
      // Corrupted auth session data
      'stale_auth_session',
      'corrupted_token',
      'error_boundary_auth',
      'auth-in-progress',
      
      // Any keys that might have been written during error monitoring
      // that could interfere with auth flow
    ];
    
    // Clean suspicious localStorage items
    suspiciousKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        console.log(`[AuthCleanup] Removing suspicious localStorage key: ${key}`);
        localStorage.removeItem(key);
      }
    });
    
    // Clean suspicious sessionStorage items
    suspiciousKeys.forEach(key => {
      if (sessionStorage.getItem(key)) {
        console.log(`[AuthCleanup] Removing suspicious sessionStorage key: ${key}`);
        sessionStorage.removeItem(key);
      }
    });
    
    // Look for any localStorage keys that might contain error monitoring artifacts
    const allLocalStorageKeys = Object.keys(localStorage);
    const errorRelatedKeys = allLocalStorageKeys.filter(key => 
      key.includes('error') || 
      key.includes('monitoring') || 
      key.includes('log') ||
      (key.startsWith('auth') && !key.includes('supabase'))  // Non-supabase auth keys are suspicious
    );
    
    errorRelatedKeys.forEach(key => {
      // Don't remove legitimate supabase auth keys
      if (!key.includes('supabase.auth') && 
          !key.includes('sb-') && 
          key !== 'auth-in-progress' && 
          key !== 'intentional_auth_redirect') {
        console.log(`[AuthCleanup] Removing error-related localStorage key: ${key}`);
        localStorage.removeItem(key);
      }
    });
    
    // Do the same for sessionStorage
    const allSessionStorageKeys = Object.keys(sessionStorage);
    const errorRelatedSessionKeys = allSessionStorageKeys.filter(key => 
      key.includes('error') || 
      key.includes('monitoring') || 
      key.includes('log') ||
      (key.startsWith('auth') && !key.includes('supabase') && !key.startsWith('ai_agent_session'))
    );
    
    errorRelatedSessionKeys.forEach(key => {
      // Don't remove legitimate keys
      if (!key.includes('supabase.auth') && 
          !key.includes('sb-') && 
          !key.includes('verifiedProfile') &&
          !key.startsWith('ai_agent_session') &&
          key !== 'intentional_auth_redirect') {
        console.log(`[AuthCleanup] Removing error-related sessionStorage key: ${key}`);
        sessionStorage.removeItem(key);
      }
    });
    
    // Mark cleanup as completed for this version
    localStorage.setItem(CLEANUP_KEY, AUTH_CLEANUP_VERSION);
    
    console.log('[AuthCleanup] ✅ AGGRESSIVE auth state cleanup completed - affected users should now be able to sign out normally');
    
  } catch (error) {
    console.error('[AuthCleanup] Error during auth state cleanup:', error);
    // Don't throw - this should be non-blocking
  }
};

/**
 * Check if user might have corrupted auth state
 * This can be used to show a helpful message to users
 */
export const hasLikelyCorruptedAuthState = () => {
  try {
    const cleanupPerformed = localStorage.getItem(CLEANUP_KEY);
    
    // If cleanup hasn't been performed, they might have corrupted state
    if (!cleanupPerformed || cleanupPerformed !== AUTH_CLEANUP_VERSION) {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[AuthCleanup] Error checking auth state:', error);
    return false;
  }
};

/**
 * EMERGENCY: Force clear ALL auth data and reload page
 * This is for users who are still stuck and can't sign out
 */
export const emergencyAuthReset = () => {
  try {
    console.log('[AuthCleanup] 🚨 EMERGENCY AUTH RESET - Clearing everything');
    
    // Clear ALL localStorage
    try {
      localStorage.clear();
      console.log('[AuthCleanup] ✅ Cleared all localStorage');
    } catch (err) {
      console.error('[AuthCleanup] Could not clear localStorage:', err);
    }
    
    // Clear ALL sessionStorage  
    try {
      sessionStorage.clear();
      console.log('[AuthCleanup] ✅ Cleared all sessionStorage');
    } catch (err) {
      console.error('[AuthCleanup] Could not clear sessionStorage:', err);
    }
    
    console.log('[AuthCleanup] 🔄 Emergency reset complete - reloading page');
    
    // Force page reload
    window.location.href = '/';
    
  } catch (error) {
    console.error('[AuthCleanup] Emergency reset failed:', error);
    // Last resort - just reload
    window.location.reload();
  }
};

// Make emergency reset available in development
if (process.env.NODE_ENV === 'development') {
  window.emergencyAuthReset = emergencyAuthReset;
  console.log('[AuthCleanup] 🆘 Emergency auth reset available: window.emergencyAuthReset()');
}
